// Step1Numbers.jsx
//
// Wizard step 1: which numbers does the customer want to port?
//
// UX:
//   - Single paste/type input that accepts commas, newlines, spaces, and
//     formatted phone numbers. On commit (Enter, blur, paste), each entry is
//     normalized to E.164 and added to the staged list.
//   - For each staged number, fire `portability-check` once. Cache the result
//     on the draft so the lookup is not re-billed on re-renders or wizard
//     re-entry. Cached responses are recognized via the presence of
//     `losing_carrier_detected_value`.
//   - Inline row UI per number: spinner while looking up, green check +
//     carrier label when portable, amber/red message when not.
//   - User can remove any row.

import React, { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, XCircle, Trash2, Phone } from 'lucide-react';
import { portabilityCheck } from '../../../../../../lib/portInClient';

/**
 * Normalize a user-entered phone token into E.164. Mirrors the server-side
 * `normalizeUSPhoneE164` in supabase/functions/_shared/portValidation.ts so
 * what the user sees in the row is what the server would accept.
 */
function normalizeToE164(input) {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  const hasPlus = raw.startsWith('+');
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (hasPlus && digits.length >= 11 && digits.length <= 15) return `+${digits}`;
  return null;
}

function splitInput(text) {
  if (!text) return [];
  return String(text)
    .split(/[\n,;\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function formatForDisplay(e164) {
  // +15551234567 -> (555) 123-4567
  if (!e164 || e164.length < 12) return e164;
  const d = e164.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return e164;
}

const REASON_COPY = {
  toll_free_not_supported:
    "Toll-free numbers are a different port-in process. Reach out to support to handle these separately.",
  premium_not_supported: 'Premium-rate numbers cannot be ported in.',
  unknown_line_type:
    "We couldn't identify this line type. Double check the number, or contact support if it looks right.",
  lookup_failed:
    "We couldn't reach the carrier lookup service right now. Try again in a minute.",
};

export default function Step1Numbers({ draftState }) {
  const { draft, upsertNumber, removeNumber, setCarrier } = draftState;
  const [rawInput, setRawInput] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const inFlightRef = useRef(new Set());

  // Background portability lookup loop. Runs every time the numbers array
  // changes; only touches entries that don't yet have a cached lookup result.
  useEffect(() => {
    if (!draft) return;
    const pending = (draft.numbers ?? []).filter(
      (n) =>
        n?.phone_number &&
        !n.losing_carrier_detected_value &&
        !n.portability &&
        !inFlightRef.current.has(n.phone_number)
    );
    if (pending.length === 0) return;

    pending.forEach(async (n) => {
      inFlightRef.current.add(n.phone_number);
      try {
        const { data, error } = await portabilityCheck(n.phone_number);
        if (error) {
          upsertNumber(n.phone_number, {
            portability: { portable: false, reason: 'lookup_failed' },
          });
          return;
        }
        const carrier = data?.carrier ?? null;
        if (carrier) setCarrier(n.phone_number, carrier);
        upsertNumber(n.phone_number, {
          portability: {
            portable: !!data?.portable,
            reason: data?.reason ?? null,
          },
          losing_carrier: carrier?.displayName ?? n.losing_carrier ?? null,
          losing_carrier_detected_value: carrier?.twilioReportedName ?? carrier?.displayName ?? null,
          losing_carrier_detected_at: new Date().toISOString(),
        });
      } finally {
        inFlightRef.current.delete(n.phone_number);
      }
    });
  }, [draft, upsertNumber]);

  const existingPhoneSet = new Set((draft?.numbers ?? []).map((n) => n.phone_number));

  const commitInput = () => {
    const tokens = splitInput(rawInput);
    if (tokens.length === 0) {
      setRawInput('');
      return;
    }
    const seen = new Set();
    const accepted = [];
    const rejected = [];
    for (const tok of tokens) {
      const e164 = normalizeToE164(tok);
      if (!e164) {
        rejected.push(tok);
        continue;
      }
      if (seen.has(e164)) continue;
      if (existingPhoneSet.has(e164)) continue;
      seen.add(e164);
      accepted.push(e164);
    }
    for (const e164 of accepted) {
      upsertNumber(e164, { phone_number: e164 });
    }
    setRawInput('');
    if (rejected.length > 0) {
      setErrorMsg(
        `Could not read: ${rejected.slice(0, 3).join(', ')}${
          rejected.length > 3 ? ` (+${rejected.length - 3} more)` : ''
        }`
      );
    } else {
      setErrorMsg('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitInput();
    }
  };

  const handleBlur = () => {
    if (rawInput.trim().length > 0) commitInput();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData?.getData('text') ?? '';
    if (!pasted) return;
    e.preventDefault();
    setRawInput((cur) => `${cur}${cur ? ' ' : ''}${pasted}`);
    setTimeout(() => commitInput(), 0);
  };

  const rows = draft?.numbers ?? [];
  const portableCount = rows.filter((n) => n?.portability?.portable === true).length;

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-5 text-sm text-blue-900">
        Your numbers stay live throughout the entire process. Customers do not
        experience downtime while the port is moving through carrier review.
      </div>

      <h3 className="text-base font-semibold text-slate-900 mb-1">
        Which numbers do you want to port?
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Paste or type one or more US phone numbers. Press Enter to add. You can
        bring in a batch in one paste.
      </p>

      <div className="flex gap-2 mb-3">
        <div className="flex-1 relative">
          <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={rawInput}
            onChange={(e) => setRawInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onPaste={handlePaste}
            placeholder="(555) 123-4567 or paste a list"
            className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>
        <button
          type="button"
          onClick={commitInput}
          className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
        >
          Add
        </button>
      </div>

      {errorMsg && (
        <p className="text-xs text-amber-700 mb-3">{errorMsg}</p>
      )}

      <div className="space-y-2">
        {rows.length === 0 && (
          <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200 text-sm text-slate-500">
            No numbers added yet. Paste or type to begin.
          </div>
        )}
        {rows.map((entry) => (
          <NumberRow
            key={entry.phone_number}
            entry={entry}
            onRemove={() => removeNumber(entry.phone_number)}
          />
        ))}
      </div>

      <div className="mt-4 text-xs text-slate-500">
        {portableCount > 0 ? (
          <span>
            {portableCount} number{portableCount === 1 ? '' : 's'} ready to continue.
          </span>
        ) : (
          <span>Add at least one portable number to continue.</span>
        )}
      </div>
    </div>
  );
}

function NumberRow({ entry, onRemove }) {
  const portability = entry?.portability;
  const looking = !portability;
  const portable = portability?.portable === true;
  const reason = portability?.reason;

  let icon;
  let badgeClass;
  let badgeText;
  if (looking) {
    icon = <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />;
    badgeClass = 'text-slate-500';
    badgeText = 'Checking portability...';
  } else if (portable) {
    icon = <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    badgeClass = 'text-emerald-700';
    badgeText = entry.losing_carrier
      ? `Portable. Currently on ${entry.losing_carrier}.`
      : 'Portable.';
  } else if (reason === 'toll_free_not_supported' || reason === 'premium_not_supported') {
    icon = <XCircle className="w-4 h-4 text-red-600" />;
    badgeClass = 'text-red-700';
    badgeText = REASON_COPY[reason];
  } else {
    icon = <AlertTriangle className="w-4 h-4 text-amber-600" />;
    badgeClass = 'text-amber-700';
    badgeText = REASON_COPY[reason] || 'This number is not portable.';
  }

  return (
    <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
      <div className="flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">
          {formatForDisplay(entry.phone_number)}
        </div>
        <div className={`text-xs mt-0.5 ${badgeClass}`}>{badgeText}</div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
        aria-label="Remove number"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
